import Table from "antd/es/table";
import { Switch, notification } from "antd";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MaterialsItem,
  useArchiveMaterialMutation,
  useGetAllMaterialsQuery,
} from "shared/api";
import { expandMaterials } from "shared/constants";
import { handleRequestError } from "shared/helpers";
import {
  useDateFilters,
  useMaterialManagementRequests,
  usePagination,
  useTableSorting,
  useUpdatedMaterialManagementsFilters,
  useUpdatedMaterialManagementTableColumnsGeneration,
} from "shared/hooks";
import { AdaptedValues } from "shared/types";
import { RenderClearFiltersButton, renderPaginationTotal } from "shared/ui";

interface UpdatedMaterialManagementTableProps {
  handleMaterialItemToEdit: (materialItem: MaterialsItem) => void;
  materialCategoriesAdapted: AdaptedValues[];
  handelMaterialStockHistoryDetails: (item: MaterialsItem) => void;
  handelMaterialConsumedDetails: (item: MaterialsItem) => void;
  handleMaterialReservationDetails: (item: MaterialsItem) => void;
  handleStockManagementDetails: (item: MaterialsItem) => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
}

const UpdatedMaterialManagementTable: FC<
  UpdatedMaterialManagementTableProps
> = ({
  handleMaterialItemToEdit,
  materialCategoriesAdapted,
  handelMaterialStockHistoryDetails,
  handelMaterialConsumedDetails,
  handleMaterialReservationDetails,
  handleStockManagementDetails,
  hasDeletePermission,
  hasEditPermission,
}) => {
  const { t } = useTranslation();

  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    usePagination();

  const { ordering, changeSort } = useTableSorting();
  const isSortingByLastConsumed = ordering?.includes("last_consumed");

  const [showArchived, setShowArchived] = useState(false);
  const [ignoreNeverConsumed, setIgnoreNeverConsumed] = useState(false);

  const {
    dateGreater: lastConsumedGreater,
    dateLesser: lastConsumedLesser,
    handleDateGreater: handleLastConsumedGreater,
    handleDateLesser: handleLastConsumedLesser,
    isDataFilterActive: isDateRangeActive,
  } = useDateFilters();

  const isLastConsumedFilterActive = ignoreNeverConsumed || isDateRangeActive;

  const {
    debouncedNameContains,
    handleNamedValue,
    nameValue,
    materialCategoryFilteringItem,
    clearAllFilters,
    isVisibleClearFiltersButton,
    clearNameValue,
    handleMaterialCategory,
  } = useUpdatedMaterialManagementsFilters();

  const {
    data: allMaterials,
    isLoading: isLoadingMaterials,
    isFetching: isFetchingMaterials,
  } = useGetAllMaterialsQuery(
    {
      offset,
      limit,
      expand: expandMaterials,
      ordering,
      name__icontains: debouncedNameContains,
      material_category: materialCategoryFilteringItem,
      last_consumed: isSortingByLastConsumed,
      is_archived: showArchived,
      calc_last_consumed__isnull: ignoreNeverConsumed ? false : undefined,
      calc_last_consumed__gte: lastConsumedGreater || undefined,
      calc_last_consumed__lte: lastConsumedLesser || undefined,
    },
    {}
  );

  const { showDeleteCategoryModal, contextHolder } =
    useMaterialManagementRequests({});

  const [archiveMaterial] = useArchiveMaterialMutation();
  const [api, archiveContextHolder] = notification.useNotification();

  const handleMaterialArchive = async (item: MaterialsItem) => {
    try {
      await archiveMaterial({
        id: item.id,
        is_archived: !item.is_archived,
      }).unwrap();
      api.success({
        message: t("material-management.success-title"),
        description: t("material-management.archive-success"),
      });
    } catch (error) {
      api.error({
        message: t("material-management.error-title"),
        description: handleRequestError(
          error,
          t("material-management.error-title")
        ),
      });
    }
  };

  const columns = useUpdatedMaterialManagementTableColumnsGeneration({
    t,
    handleMaterialItemToEdit,
    changeSort,
    nameValue,
    clearNameValue,
    handleNamedValue,
    materialCategoriesAdapted,
    handleMaterialCategory,
    materialCategoryFilteringItem,
    handelMaterialStockHistoryDetails,
    handelMaterialConsumedDetails,
    handleMaterialReservationDetails,
    handleStockManagementDetails,
    showDeleteCategoryModal,
    handleMaterialArchive,
    hasDeletePermission,
    hasEditPermission,
    ignoreNeverConsumed,
    handleIgnoreNeverConsumed: setIgnoreNeverConsumed,
    handleLastConsumedGreater,
    handleLastConsumedLesser,
    isLastConsumedFilterActive,
  });

  return (
    <>
      {contextHolder}
      {archiveContextHolder}

      <div className="mb-4 flex items-center justify-between">
        <div>
          {isVisibleClearFiltersButton && (
            <RenderClearFiltersButton onClick={clearAllFilters} />
          )}
        </div>
        <div className="p-3 flex items-center gap-2 justify-between bg-gray-50 rounded-lg border border-gray-300 shadow-sm">
          <span className="font-semibold text-gray-700 text-sm">
            {t("material-management.show-archived")}
          </span>
          <Switch
            style={{
              backgroundColor: showArchived ? "#002F5F" : "#888d94",
            }}
            checked={showArchived}
            onChange={(checked) => setShowArchived(checked)}
            size="default"
          />
        </div>
      </div>

      <Table
        loading={isLoadingMaterials || isFetchingMaterials}
        columns={columns}
        dataSource={allMaterials?.results}
        rowKey={(obj) => obj.id!}
        onRow={(record: MaterialsItem) => ({
          style: {
            backgroundColor: record.is_archived ? "grey" : "transparent",
            color: record.is_archived ? "white" : "inherit",
          },
        })}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: allMaterials?.count,
          showTotal: renderPaginationTotal,
        }}
      />
    </>
  );
};

export { UpdatedMaterialManagementTable };
